import range from "lodash/range";

import toArray from "lodash/toArray";

import { useEffect, useState } from "react";

import { useTranslator } from "@eo-locale/react";

import { CommonRankingQuestion } from "@/storybook-components";
import { questionNumber, questionTitleWithNumber } from "@/helpers/survey";

import { setSurveyAnswers } from "@/redux/features/survey/slice";
import type { Question } from "@/types";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";

import useEmbeds from "@/hooks/useEmbeds";
import googleTranslateInit from "@/helpers/google-translate-init";
import { spacing, textStyles } from "./question-styles";
import { surveyParticipantFieldControl } from "./survey-participant-test-ids";

const hashifyAnswer = (array = []) => {
  return array.reduce((a, b, index) => {
    a[b] = index + 1;
    return a;
  }, {});
};

const flattenAnswer = (object) => {
  return Object.keys(object)
    .sort((a, b) => object[a] - object[b])
    .map(Number);
};

interface RankingQuestionProps {
  question: Question;
  participantFieldId: string;
}

const RankingQuestion = ({
  question,
  participantFieldId
}: RankingQuestionProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const { translate } = useTranslator();
  const { isEmbed } = useEmbeds();
  const survey = useAppSelector((state) => state.survey);
  const { answers, errors, options, saving } = survey;
  const { settings } = useAppSelector((state) => state.settings);
  const { googleTranslationEnabled } = settings;

  const [answer, setAnswer] = useState(
    answers[question.id] ? hashifyAnswer(answers[question.id].id) : {}
  );

  const error = errors[question.id] || {};

  // ToDo: Remove after BE implementation
  const sortedOptions = toArray(options)
    .filter((option) => question.id === option.questionId)
    .sort((a, b) => (a.sequence > b.sequence ? 1 : -1));

  const handleChange = (e: { value: string }, optionId: string) => {
    if (!e) {
      // need to trigger Google Translate upon clearing the value to translate the placeholder
      googleTranslateInit(isEmbed, googleTranslationEnabled);
      delete answer[optionId];
      return setAnswer({ ...answer });
    }

    return setAnswer({
      ...answer,
      [optionId]: parseInt(e.value, 10)
    });
  };

  useEffect(() => {
    dispatch(
      setSurveyAnswers({
        questionId: question.id,
        answer: {
          id: flattenAnswer(answer)
        }
      })
    );
  }, [answer, dispatch, question.id]);

  const rankRange = range(1, sortedOptions.length + 1).map((rank) => ({
    label: rank,
    value: rank
  }));

  return (
    <CommonRankingQuestion
      layoutProps={{ width: "100%", as: "fieldset" }}
      invalid={!!error.code}
      placeholder={translate("common.select")}
      required={question.required}
      className={error.code ? "ehq-question-error" : null}
      gap={spacing.question}
      questionLabel={questionTitleWithNumber(
        questionNumber(question, survey),
        question
      )}
      questionLabelType="legend"
      questionLabelProps={{ ...textStyles?.label }}
      labelClassName="ehq-surveyQuestionHeading"
      accessibilityTextOfRequired={`* ${translate("common.required")}`}
      contentLayoutProps={{ align: { lg: "flex-start", sm: "stretch" }, py: 4 }}
      answer={answer}
      size="lg"
      isClearable={true}
      rankRange={rankRange}
      handleChange={handleChange}
      options={sortedOptions}
      answerProps={textStyles?.content}
      answerLabelProps={{ ...textStyles?.contentLabel }}
      questionId={question?.id}
      disabled={saving}
      answerStackSpacing={spacing.answerStack}
      answerGroupSpacing={spacing.answerGroup}
      footerStackSpacing={spacing.footerStack}
      note={question.notes}
      noteProps={{ ...textStyles?.footer }}
      errorMessage={error?.code && translate(`errors.${error.code}`)}
      errorMessageProps={{ ...textStyles?.error }}
      getRankingSelectDataTestId={(optionId) =>
        surveyParticipantFieldControl(
          participantFieldId,
          `ranking-option-${sortedOptions.findIndex(
            (o) => String(o.id) === String(optionId)
          )}-select`
        )
      }
    />
  );
};

export default RankingQuestion;
